import React from "react";
import { CalendarX2 } from "lucide-react";

const KIND_LABELS = {
  holiday: "Férié",
  annual_closure: "Fermeture annuelle",
  exceptional_closure: "Fermeture exceptionnelle",
};

/** Rendu du bandeau pour un seul groupe (label + kind + color communs). */
function BannerRow({ group, compact, showBrands, brands, testIdPrefix }) {
  const { label, kind, color = "#DC2626", entries } = group;
  const brandNames = showBrands
    ? entries.map((e) => brands.find((b) => b.id === e.brand_id)?.name).filter(Boolean)
    : [];

  const bgStyle = {
    background: `repeating-linear-gradient(45deg, ${color}22 0 8px, ${color}11 8px 16px)`,
    borderLeft: `3px solid ${color}`,
  };

  if (compact) {
    return (
      <div
        className="w-full px-1.5 py-0.5 rounded-sm text-[10px] font-mono flex items-center gap-1 truncate"
        style={bgStyle}
        title={`${KIND_LABELS[kind] || "Non travaillé"} — ${label}`}
        data-testid={`${testIdPrefix}-badge-${entries[0].date}`}
      >
        <CalendarX2 size={10} style={{ color }} />
        <span className="truncate font-semibold" style={{ color }}>{label}</span>
      </div>
    );
  }

  return (
    <div
      className="w-full px-3 py-2 rounded-sm text-xs font-mono flex items-center gap-2"
      style={bgStyle}
      data-testid={`${testIdPrefix}-banner-${entries[0].date}`}
    >
      <CalendarX2 size={14} style={{ color }} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold uppercase tracking-wider text-[10px]" style={{ color }}>
          {KIND_LABELS[kind] || "Non travaillé"}
        </p>
        <p className="text-zinc-800 truncate">{label}</p>
        {brandNames.length > 0 && (
          <p className="text-[10px] text-zinc-600 truncate">
            {brandNames.join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Bandeau "Jour non travaillé" — affichage compact en tête d'une case de jour.
 * Utilisé sur Planning collaborateurs, Agenda départements et Mon planning.
 *
 * Regroupe les `entries` par (label + kind + color) pour afficher un bandeau
 * par étiquette distincte. En mode "Tous les départements", cela permet
 * d'afficher côte à côte "Férié — Noël" (Marque A) et "Fermeture annuelle —
 * Congés d'entreprise" (Marque B) le même jour, sans en masquer aucun.
 */
export default function NonWorkingDayBanner({
  entries,
  compact = false,
  showBrands = false,
  brands = [],
  testIdPrefix = "nwd",
}) {
  if (!entries || entries.length === 0) return null;

  const groups = [];
  const byKey = new Map();
  for (const e of entries) {
    const key = `${e.label}||${e.kind}||${e.color || ""}`;
    if (!byKey.has(key)) {
      const g = { label: e.label, kind: e.kind, color: e.color, entries: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    byKey.get(key).entries.push(e);
  }

  if (groups.length === 1) {
    return (
      <BannerRow
        group={groups[0]}
        compact={compact}
        showBrands={showBrands}
        brands={brands}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  return (
    <div className={compact ? "space-y-0.5" : "space-y-1"}>
      {groups.map((g, i) => (
        <BannerRow
          key={`${g.label}-${g.kind}-${i}`}
          group={g}
          compact={compact}
          showBrands={showBrands}
          brands={brands}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}

export { KIND_LABELS };
